import TimeAgo from 'javascript-time-ago'
import { createAsync, query, revalidate, useParams } from "@solidjs/router";
import { createMemo, createEffect, createSignal, For, Show } from "solid-js";
import { FileText } from "lucide-solid";
import { amendmentCount, certificateCost, CertificateState, padId, RIASRecordType, schemeData, stageCount } from "~/lib";
import { createMissingCertificateInvoice } from "~/lib/businessLogic";

const { getByUser } = await import( "~/_server/dynamoDB" );
import "~/_assets/admin.css";

const certificatesQuery = query( async ( userId: number ) => {
	"use server";

	console.log( `Getting certifier ${RIASRecordType.CERTIFICATE} for user`, userId );

	return await getByUser( RIASRecordType.CERTIFICATE, userId, -1 );
}, "getCertifierCertificates" );


const subscriptionsQuery = query( async ( userId: number ) => {
	"use server";

	console.log( `Getting certifier ${RIASRecordType.SUBSCRIPTION} for user`, userId );

	return await getByUser( RIASRecordType.SUBSCRIPTION, userId, -1 );
}, "getCertifierSubscriptions" );



export const CertifierCertificates = () => {
	const { id } = useParams();
	const timeAgo = new TimeAgo( 'en-GB' );
	const certificates = createAsync( () => certificatesQuery( parseInt( id ?? "0" ) ) );

	return (
		<table id="certificates-table" class="table-fixed w-full border-collapse text-[13px]" aria-label="Certificates list">
			<thead>
				<tr>
					<th class="text-left w-23">Status</th>
					<th class="text-left w-32">Certificate</th>
					<th class="text-left">Project</th>
					<th class="text-right w-16">Plots</th>
					<th class="text-right w-22">Fee</th>
					<th class="text-right w-26">Issued</th>
					<th class="w-10"><span class="sr-only">PDF</span></th>
				</tr>
			</thead>
			<tbody>
				<Show when={certificates()?.items?.length}>
					<For each={certificates().items}>{c => <CertificateRow c={c} timeAgo={timeAgo} />}</For>
				</Show>
			</tbody>
		</table>
	)
}


// Letter and colour for the scheme chip, keyed on the slug stored on the record.
// An unrecognised scheme gets a neutral "?" rather than silently claiming Domestic.
const SCHEME_CHIPS: Record<string, { letter: string, classes: string }> = {
	"section-6-domestic": { letter: "D", classes: "bg-blue-100 text-blue-700" },
	"section-6-non-domestic": { letter: "N", classes: "bg-amber-100 text-amber-700" },
	"section-6-non-domestic-advanced": { letter: "A", classes: "bg-purple-100 text-purple-700" },
	"cpd-membership": { letter: "C", classes: "bg-teal-100 text-teal-700" },
};

const UNKNOWN_CHIP = { letter: "?", classes: "bg-gray-100 text-gray-600" };


const CertificateRow = ( props: any ) => {
	const [creating, setCreating] = createSignal( false );
	const [invoiceError, setInvoiceError] = createSignal( '' );

	// Published but never invoiced - the Xero call failed at publish time (a
	// same-day amendment used to collide with its parent's invoice number).
	const needsInvoice = () => props.c.status === CertificateState.PUBLISHED && !props.c.invoiceId && !!props.c.cost;

	const handleCreateInvoice = async () => {
		if ( !confirm( `Create the Xero invoice for ${props.c.name}?\n\nThe certifier will then be able to pay it from their dashboard.` ) ) return;

		setCreating( true );
		setInvoiceError( '' );

		try {
			const r = await createMissingCertificateInvoice( props.c.pk, props.c.sk );

			if ( !r?.invoiceId ) throw new Error( r?.error ?? 'No invoice id returned' );

			await revalidate( 'getCertifierCertificates' );
		} catch ( err ) {
			console.error( 'Create invoice failed', err );
			setInvoiceError( 'Invoice creation failed - check the server log.' );
		} finally {
			setCreating( false );
		}
	};

	const memoized = createMemo( () => {
		const isDraft = props.c.status === CertificateState.DRAFT;
		const isPaid = props.c.status === CertificateState.PAID;
		const isPublished = props.c.status === CertificateState.PUBLISHED || isPaid;
		const isStaged = !!props.c.meta.isStagedCertificate;
		const stages = stageCount( props.c );
		const amendments = amendmentCount( props.c );

		// Same id string the /certificate/[id] route parses: C0002956-Stage2-Amendment1
		let link = props.c.riasId ? `C${padId( props.c.riasId )}` : "";

		if ( link && stages ) link += `-Stage${stages}`;
		if ( link && amendments ) link += `-Amendment${amendments}`;

		// Legacy certificates carry no fee, so fall back to what the current
		// fee schedule would charge for this plot/stage/amendment combination.
		const storedFee = props.c.meta?.certificateFee ?? props.c.cost;
		const canEstimate = ["section-6-domestic", "section-6-non-domestic"].includes( props.c.scheme );
		const rawFee = storedFee ?? ( canEstimate ? certificateCost( props.c ) : null );

		// Whole pounds - this column is a guide, not an invoice line.
		const fee = rawFee == null ? null : Math.round( rawFee );

		// Tooltip comes from schemes.json so it stays in step with the fee schedule.
		const chip = SCHEME_CHIPS[props.c.scheme] ?? UNKNOWN_CHIP;
		const schemeName = schemeData( props.c.scheme )?.name ?? props.c.scheme ?? "Unknown scheme";

		return {
			chip,
			schemeName,
			isDraft,
			isPaid,
			isPublished,
			isStaged,
			stageCount: stages + 1,
			amendCount: amendments + 1,
			link,
			fee,
			feeEstimated: storedFee == null && fee != null,
		}
	} );

	return (
		<tr data-id="C0002956" class="certificate-row" tabindex="0" role="row" aria-selected="false">
			<td class="px-3 py-1.75 border-b border-border align-middle text-center">
				<span class={`cert-status ${props.c.status}`}>{props.c.status}</span>
			</td>
			<td class="px-3 py-1.75 border-b border-border align-middle">
				<div class="flex items-center gap-1.5">
					<span
						class={`inline-flex items-center justify-center w-[18px] h-[18px] rounded-[4px] text-[9.5px] font-bold shrink-0 ${memoized().chip.classes}`}
						title={memoized().schemeName}>
						{memoized().chip.letter}
					</span>
					<span class="font-mono text-[11.5px] text-text-2">C{padId( props.c.riasId )}</span>
				</div>
			</td>
			<td class="px-3 py-1.75 border-b border-border align-middle text-[13px] whitespace-nowrap overflow-hidden text-ellipsis">
				{props.c.name}
				<Show when={needsInvoice()}>
					<button
						type="button"
						disabled={creating()}
						onClick={handleCreateInvoice}
						title={invoiceError() || 'This certificate was published without an invoice. Create it in Xero now.'}
						class="ml-3 align-middle rounded px-2 py-0.5 text-[11px] font-medium border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-50"
						classList={{ 'border-red-300 bg-red-50 text-red-700': !!invoiceError() }}>
						{creating() ? 'Creating…' : invoiceError() ? 'Failed' : 'Create invoice'}
					</button>
				</Show>
			</td>
			<td class="px-3 py-1.75 border-b border-border align-middle text-[13px] text-right">{props.c.meta.plots.length}</td>
			<td class="px-3 py-1.75 border-b border-border align-middle text-[13px] text-right">
				<Show when={memoized().fee != null} fallback="-">
					<span
						classList={{ "italic text-text-3": memoized().feeEstimated }}
						title={memoized().feeEstimated ? "Estimated from the current fee schedule - no fee stored on this certificate" : undefined}>
						{memoized().feeEstimated ? "~" : ""}£{memoized().fee}
					</span>
				</Show>
			</td>
			<td class="px-3 py-1.75 border-b border-border align-middle text-[11px] text-text-3 text-right">{props.timeAgo.format( props.c.created * 1000 )}</td>
			<td class="px-3 py-1.75 border-b border-border align-middle text-center">
				<Show when={memoized().link}>
					<a
						href={`/certificate/${memoized().link}`}
						target="_blank"
						rel="noopener noreferrer"
						title={`Open PDF ${memoized().link}`}
						class="inline-flex items-center justify-center rounded p-1 text-text-3 hover:bg-blue-subtle hover:text-blue-text">
						<FileText class="h-4 w-4" aria-hidden="true" />
						<span class="sr-only">Open PDF</span>
					</a>
				</Show>
			</td>
		</tr>
	)
}